import { useMemo, useState } from 'react';
import { Button, Card } from '@safetyhub/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Site } from '../../../../lib/organizations';
import { organizationsApi } from '../../../../lib/organizations';
import { siteIndustryLabel } from '../../../../lib/siteIndustry';
import { DirtyHint } from '../../../../components/common/DirtyHint';
import type { WorkerStatsEntry, AccidentStatsEntry } from '../../../../types/site-stats';
import {
  ACCIDENT_COUNT_FIELDS,
  SMS_WORKER_CATEGORIES,
  createEmptyAccidentEntry,
  createEmptyWorkerEntry,
} from '../../../../types/site-stats';
import {
  getWorkerStats,
  upsertWorkerStats,
  deleteWorkerStats,
  getAccidentStats,
  upsertAccidentStats,
  deleteAccidentStats,
} from '../../../../lib/site-stats';
import { extractApiError } from '../../../../lib/api';
import { isSiteInfoLocked, siteInfoLockedReason } from '../../../../lib/siteInfoManage';

interface SiteBusinessOverviewTabProps {
  companyId: string;
  siteId: string;
  site: Site;
  isOwner: boolean;
}

/**
 * 수정 dirty 판정용 스냅샷 — **편집 가능한 입력 전수**만 담는다(계·재해율은 파생값이라 제외).
 * 하나라도 빠뜨리면 "바꿨는데 저장이 안 눌리는" 반대 방향 결함이 되므로 폼 입력과 1:1로 맞춘다.
 *
 * 폼이 핸들러(수정 클릭)에서 열리고 그 시점에 원본 행이 손에 있어, 표준 훅(`useDirtySnapshot`)의
 * "렌더 후 capture" 대신 원본 행에서 기준점을 바로 만든다(결정 기록의 스냅샷 비교와 같은 방식).
 */
function workerRowSnapshot(row: WorkerStatsEntry): string {
  return JSON.stringify({
    periodValue: row.periodValue,
    counts: SMS_WORKER_CATEGORIES.map(
      (cat) => row.categories.find((c) => c.key === cat.key)?.count ?? 0,
    ),
  });
}

function accidentRowSnapshot(row: AccidentStatsEntry): string {
  return JSON.stringify({
    periodValue: row.periodValue,
    counts: ACCIDENT_COUNT_FIELDS.map(({ key }) => row[key]),
  });
}

export function SiteBusinessOverviewTab({
  companyId,
  siteId,
  site,
  isOwner,
}: SiteBusinessOverviewTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: organization } = useQuery({
    queryKey: ['organization', companyId],
    queryFn: () => organizationsApi.findById(companyId),
    enabled: !!companyId,
  });

  const [workerError, setWorkerError] = useState('');
  const [accidentError, setAccidentError] = useState('');

  // 근로자 현황 편집 상태 (baseline = 수정 진입 시점 원본 스냅샷, 추가 모드는 null)
  const [editingWorkerRow, setEditingWorkerRow] = useState<WorkerStatsEntry | null>(null);
  const [workerEditMode, setWorkerEditMode] = useState<'add' | 'edit' | null>(null);
  const [workerBaseline, setWorkerBaseline] = useState<string | null>(null);

  // 재해발생 현황 편집 상태
  const [editingAccidentRow, setEditingAccidentRow] = useState<AccidentStatsEntry | null>(null);
  const [accidentEditMode, setAccidentEditMode] = useState<'add' | 'edit' | null>(null);
  const [accidentBaseline, setAccidentBaseline] = useState<string | null>(null);

  // 근로자 현황 데이터 조회
  const { data: workerStats = [], isLoading: workerLoading } = useQuery({
    queryKey: ['workerStats', companyId, siteId],
    queryFn: async () => {
      const res = await getWorkerStats(companyId, siteId);
      return res.data ?? [];
    },
    enabled: !!companyId && !!siteId,
  });

  // 재해발생 현황 데이터 조회
  const { data: accidentStats = [], isLoading: accidentLoading } = useQuery({
    queryKey: ['accidentStats', companyId, siteId],
    queryFn: async () => {
      const res = await getAccidentStats(companyId, siteId);
      return res.data ?? [];
    },
    enabled: !!companyId && !!siteId,
  });

  // 표시 순서: 연도 오름차순(전년→당해, 최신이 나중) — 보고서 양식(구 SMS 사업장 개요)·composer
  // 작성 표와 통일(2026-07-15 사용자 확정). API는 최신 먼저(desc — 카탈로그 인덱스 등 타 소비처
  // 의존)라 건드리지 않고 이 화면 표시에서만 정렬. periodValue=연도 문자열(자릿수 동일)이라 사전순=연도순.
  const workerStatsAsc = useMemo(
    () => [...workerStats].sort((a, b) => a.periodValue.localeCompare(b.periodValue)),
    [workerStats],
  );
  const accidentStatsAsc = useMemo(
    () => [...accidentStats].sort((a, b) => a.periodValue.localeCompare(b.periodValue)),
    [accidentStats],
  );

  // 근로자 현황 upsert
  const workerUpsertMutation = useMutation({
    mutationFn: (dto: WorkerStatsEntry) => upsertWorkerStats(companyId, siteId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workerStats', companyId, siteId] });
      setEditingWorkerRow(null);
      setWorkerEditMode(null);
      setWorkerBaseline(null);
      setWorkerError('');
    },
    onError: (err) => {
      setWorkerError(extractApiError(err).message);
    },
  });

  // 근로자 현황 삭제
  const workerDeleteMutation = useMutation({
    mutationFn: (statsId: string) => deleteWorkerStats(companyId, siteId, statsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workerStats', companyId, siteId] });
    },
    onError: (err) => {
      setWorkerError(extractApiError(err).message);
    },
  });

  // 재해발생 현황 upsert
  const accidentUpsertMutation = useMutation({
    mutationFn: (dto: AccidentStatsEntry) => upsertAccidentStats(companyId, siteId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accidentStats', companyId, siteId] });
      setEditingAccidentRow(null);
      setAccidentEditMode(null);
      setAccidentBaseline(null);
      setAccidentError('');
    },
    onError: (err) => {
      setAccidentError(extractApiError(err).message);
    },
  });

  // 재해발생 현황 삭제
  const accidentDeleteMutation = useMutation({
    mutationFn: (statsId: string) => deleteAccidentStats(companyId, siteId, statsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accidentStats', companyId, siteId] });
    },
    onError: (err) => {
      setAccidentError(extractApiError(err).message);
    },
  });

  // 근로자 현황 유틸
  const getWorkerCategoryCount = (row: WorkerStatsEntry, key: string): number =>
    row.categories.find((c) => c.key === key)?.count ?? 0;

  const getWorkerTotal = (row: WorkerStatsEntry): number =>
    row.categories.reduce((sum, c) => sum + c.count, 0);

  // 재해발생 현황 유틸
  const getAccidentTotal = (row: AccidentStatsEntry): number =>
    row.deathCount + row.injuryCount + row.diseaseCount;

  const getWorkerCountForAccident = (periodValue: string): number => {
    const ws = workerStats.find((w) => w.periodValue === periodValue);
    return ws ? ws.categories.reduce((sum, c) => sum + c.count, 0) : 0;
  };

  const getAccidentRate = (row: AccidentStatsEntry): string => {
    const wc = getWorkerCountForAccident(row.periodValue);
    if (wc === 0) return '-';
    return ((getAccidentTotal(row) / wc) * 100).toFixed(2);
  };

  // 근로자 현황 편집 핸들러
  const handleWorkerCategoryChange = (catKey: string, raw: string) => {
    if (!editingWorkerRow) return;
    const num = parseInt(raw, 10);
    const count = isNaN(num) ? 0 : Math.max(0, num);
    setEditingWorkerRow({
      ...editingWorkerRow,
      categories: editingWorkerRow.categories.map((c) =>
        c.key === catKey ? { ...c, count } : c,
      ),
    });
  };

  // 수정 모드에서 값이 진입 시점 그대로면 저장을 막는다(추가 모드는 게이트 비대상).
  const isWorkerUnchanged =
    workerEditMode === 'edit' &&
    !!editingWorkerRow &&
    workerBaseline !== null &&
    workerRowSnapshot(editingWorkerRow) === workerBaseline;

  const handleWorkerSave = () => {
    // 버튼 disabled를 우회하는 경로까지 무변경 저장을 막는다.
    if (!editingWorkerRow || isWorkerUnchanged) return;
    workerUpsertMutation.mutate(editingWorkerRow);
  };

  const handleWorkerAddNew = () => {
    const currentYear = new Date().getFullYear();
    setEditingWorkerRow(createEmptyWorkerEntry(String(currentYear)));
    setWorkerEditMode('add');
    setWorkerBaseline(null);
    setWorkerError('');
  };

  const handleWorkerEdit = (row: WorkerStatsEntry) => {
    setEditingWorkerRow({ ...row });
    setWorkerEditMode('edit');
    setWorkerBaseline(workerRowSnapshot(row));
    setWorkerError('');
  };

  const handleWorkerCancel = () => {
    setEditingWorkerRow(null);
    setWorkerEditMode(null);
    setWorkerBaseline(null);
    setWorkerError('');
  };

  // 재해발생 현황 편집 핸들러
  const handleAccidentFieldChange = (field: keyof AccidentStatsEntry, raw: string) => {
    if (!editingAccidentRow) return;
    const num = parseInt(raw, 10);
    setEditingAccidentRow({
      ...editingAccidentRow,
      [field]: isNaN(num) ? 0 : Math.max(0, num),
    });
  };

  const isAccidentUnchanged =
    accidentEditMode === 'edit' &&
    !!editingAccidentRow &&
    accidentBaseline !== null &&
    accidentRowSnapshot(editingAccidentRow) === accidentBaseline;

  const handleAccidentSave = () => {
    if (!editingAccidentRow || isAccidentUnchanged) return;
    accidentUpsertMutation.mutate(editingAccidentRow);
  };

  const handleAccidentAddNew = () => {
    const currentYear = new Date().getFullYear();
    setEditingAccidentRow({
      ...createEmptyAccidentEntry(String(currentYear)),
      accidentRate: undefined,
    });
    setAccidentEditMode('add');
    setAccidentBaseline(null);
    setAccidentError('');
  };

  const handleAccidentEdit = (row: AccidentStatsEntry) => {
    setEditingAccidentRow({ ...row });
    setAccidentEditMode('edit');
    setAccidentBaseline(accidentRowSnapshot(row));
    setAccidentError('');
  };

  const handleAccidentCancel = () => {
    setEditingAccidentRow(null);
    setAccidentEditMode(null);
    setAccidentBaseline(null);
    setAccidentError('');
  };

  return (
    <div className="space-y-6">
      {/* A. 사업장 기본 정보 */}
      <Card padding="xl" radius="lg" elevation="md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-medium text-ink-900">사업장 기본 정보</h3>
          {isOwner &&
            // O10 — 정보 관리권이 업체에 있으면 비활성 + 사유(풀릴 수 있는 막힘이라 감추지 않는다 · 상세 헤더 「수정」과 같은 판정)
            (isSiteInfoLocked(site) ? (
              <button
                type="button"
                disabled
                title={siteInfoLockedReason()}
                className="text-sm text-ink-300 cursor-not-allowed"
              >
                수정하기
              </button>
            ) : (
              <button
                onClick={() => navigate(`/sites/${siteId}/edit`)}
                className="text-sm text-brand-600 hover:text-brand-800"
              >
                수정하기
              </button>
            ))}
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-ink-500">사업장명</dt>
            <dd className="mt-1 text-sm text-ink-900">{site.name}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-ink-500">사업자번호</dt>
            <dd className="mt-1 text-sm text-ink-900">
              {/* 사업장 개요의 번호 축은 수검업체(site.company)다 — 운영 조직 번호를 먼저 읽으면
                  타사 업체 현장에서 우리 번호가 뜬다(자사 현장은 둘이 같아 안 드러났던 기존 결함,
                  2026-08-14 실측 발견). 운영 조직은 수검업체 정보 부재 시 폴백. */}
              {site.company?.registrationNumber || organization?.company?.registrationNumber || '-'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-ink-500">소재지</dt>
            <dd className="mt-1 text-sm text-ink-900">{site.address || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-ink-500">업종</dt>
            <dd className="mt-1 text-sm text-ink-900">
              {siteIndustryLabel(site) ?? '-'}
            </dd>
          </div>
        </dl>
      </Card>

      {/* B. 근로자 현황 */}
      <Card padding="xl" radius="lg" elevation="md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-medium text-ink-900">근로자 현황</h3>
          {isOwner && workerEditMode === null && (
            <button
              onClick={handleWorkerAddNew}
              className="text-sm text-brand-600 hover:text-brand-800"
            >
              + 추가
            </button>
          )}
        </div>

        {workerError && (
          <p className="mb-3 text-sm text-red-600">{workerError}</p>
        )}

        {/* 추가/편집 폼 */}
        {workerEditMode !== null && editingWorkerRow && (
          <div className="mb-4 p-4 border border-brand-200 rounded-card bg-brand-50">
            <p className="text-sm font-medium text-ink-700 mb-3">
              {workerEditMode === 'add' ? '새 행 추가' : `${editingWorkerRow.periodValue}년 수정`}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="block text-xs text-ink-500 mb-1">연도</label>
                <input
                  type="text"
                  value={editingWorkerRow.periodValue}
                  onChange={(e) =>
                    setEditingWorkerRow({ ...editingWorkerRow, periodValue: e.target.value })
                  }
                  placeholder="예: 2026"
                  className="w-full px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              {SMS_WORKER_CATEGORIES.map((cat) => (
                <div key={cat.key}>
                  <label className="block text-xs text-ink-500 mb-1">{cat.label}(명)</label>
                  <input
                    type="number"
                    min={0}
                    value={getWorkerCategoryCount(editingWorkerRow, cat.key)}
                    onChange={(e) => handleWorkerCategoryChange(cat.key, e.target.value)}
                    className="w-full px-2 py-1.5 border border-ink-300 rounded-control text-sm text-right focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs text-ink-500 mb-1">계(명)</label>
                <span className="block w-full px-2 py-1.5 text-sm text-right text-ink-700 bg-ink-100 border border-ink-200 rounded-control">
                  {getWorkerTotal(editingWorkerRow)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleWorkerSave}
                disabled={workerUpsertMutation.isPending || isWorkerUnchanged}
              >
                {workerUpsertMutation.isPending ? '저장 중...' : '저장'}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleWorkerCancel}>
                취소
              </Button>
              {/* 저장이 왜 막혔는지 보이지 않으면 "버튼이 죽었다"로 읽힌다 — 이유를 같이 띄운다. */}
              {workerEditMode === 'edit' && (
                <DirtyHint isDirty={!isWorkerUnchanged} className="text-xs" />
              )}
            </div>
          </div>
        )}

        {/* 테이블 */}
        {workerLoading ? (
          <p className="text-sm text-ink-400 text-center py-4">불러오는 중...</p>
        ) : workerStats.length === 0 && workerEditMode === null ? (
          <p className="text-sm text-ink-500 text-center py-4">등록된 근로자 현황이 없습니다.</p>
        ) : (
          <div className="overflow-x-auto rounded-card border border-line">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-surface-soft border-b border-line">
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">연도</th>
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">계(명)</th>
                  {SMS_WORKER_CATEGORIES.map((cat) => (
                    <th key={cat.key} className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">
                      {cat.label}
                    </th>
                  ))}
                  {isOwner && (
                    <th className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">
                      관리
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-line-soft bg-white">
                {workerStatsAsc.map((row) => (
                  <tr key={row.periodValue} className="hover:bg-surface-hover transition-colors">
                    <td className="px-3 py-2 text-center text-sm text-ink-700">{row.periodValue}년</td>
                    <td className="px-3 py-2 text-center text-sm text-ink-700">{getWorkerTotal(row)}</td>
                    {SMS_WORKER_CATEGORIES.map((cat) => (
                      <td key={cat.key} className="px-3 py-2 text-center text-sm text-ink-700">
                        {getWorkerCategoryCount(row, cat.key)}
                      </td>
                    ))}
                    {isOwner && (
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleWorkerEdit(row)}
                            className="text-xs text-brand-600 hover:text-brand-800"
                          >
                            수정
                          </button>
                          <button
                            onClick={() => row.id && workerDeleteMutation.mutate(row.id)}
                            disabled={workerDeleteMutation.isPending || !row.id}
                            className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* C. 재해발생 현황 */}
      <Card padding="xl" radius="lg" elevation="md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-medium text-ink-900">재해발생 현황</h3>
          {isOwner && accidentEditMode === null && (
            <button
              onClick={handleAccidentAddNew}
              className="text-sm text-brand-600 hover:text-brand-800"
            >
              + 추가
            </button>
          )}
        </div>

        {accidentError && (
          <p className="mb-3 text-sm text-red-600">{accidentError}</p>
        )}

        {/* 추가/편집 폼 */}
        {accidentEditMode !== null && editingAccidentRow && (
          <div className="mb-4 p-4 border border-brand-200 rounded-card bg-brand-50">
            <p className="text-sm font-medium text-ink-700 mb-3">
              {accidentEditMode === 'add' ? '새 행 추가' : `${editingAccidentRow.periodValue}년 수정`}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="block text-xs text-ink-500 mb-1">연도</label>
                <input
                  type="text"
                  value={editingAccidentRow.periodValue}
                  onChange={(e) =>
                    setEditingAccidentRow({ ...editingAccidentRow, periodValue: e.target.value })
                  }
                  placeholder="예: 2026"
                  className="w-full px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              {ACCIDENT_COUNT_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-ink-500 mb-1">{label}</label>
                  <input
                    type="number"
                    min={0}
                    value={editingAccidentRow[key]}
                    onChange={(e) => handleAccidentFieldChange(key, e.target.value)}
                    className="w-full px-2 py-1.5 border border-ink-300 rounded-control text-sm text-right focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs text-ink-500 mb-1">계</label>
                <span className="block w-full px-2 py-1.5 text-sm text-right text-ink-700 bg-ink-100 border border-ink-200 rounded-control">
                  {getAccidentTotal(editingAccidentRow)}
                </span>
              </div>
              <div>
                <label className="block text-xs text-ink-500 mb-1">재해율(%)</label>
                <span className="block w-full px-2 py-1.5 text-sm text-right text-ink-700 bg-ink-100 border border-ink-200 rounded-control">
                  {getAccidentRate(editingAccidentRow)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleAccidentSave}
                disabled={accidentUpsertMutation.isPending || isAccidentUnchanged}
              >
                {accidentUpsertMutation.isPending ? '저장 중...' : '저장'}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleAccidentCancel}>
                취소
              </Button>
              {accidentEditMode === 'edit' && (
                <DirtyHint isDirty={!isAccidentUnchanged} className="text-xs" />
              )}
            </div>
          </div>
        )}

        {/* 테이블 */}
        {accidentLoading ? (
          <p className="text-sm text-ink-400 text-center py-4">불러오는 중...</p>
        ) : accidentStats.length === 0 && accidentEditMode === null ? (
          <p className="text-sm text-ink-500 text-center py-4">등록된 재해발생 현황이 없습니다.</p>
        ) : (
          <div className="overflow-x-auto rounded-card border border-line">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-surface-soft border-b border-line">
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">연도</th>
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">근로자수</th>
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">계</th>
                  {ACCIDENT_COUNT_FIELDS.map(({ label }) => (
                    <th key={label} className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">
                      {label}
                    </th>
                  ))}
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">재해율(%)</th>
                  {isOwner && (
                    <th className="px-3 py-2.5 text-center text-xs font-medium text-ink-500 whitespace-nowrap">
                      관리
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-line-soft bg-white">
                {accidentStatsAsc.map((row) => (
                  <tr key={row.periodValue} className="hover:bg-surface-hover transition-colors">
                    <td className="px-3 py-2 text-center text-sm text-ink-700">{row.periodValue}년</td>
                    <td className="px-3 py-2 text-center text-sm text-ink-700">
                      {getWorkerCountForAccident(row.periodValue)}
                    </td>
                    <td className="px-3 py-2 text-center text-sm text-ink-700">
                      {getAccidentTotal(row)}
                    </td>
                    {ACCIDENT_COUNT_FIELDS.map(({ key }) => (
                      <td key={key} className="px-3 py-2 text-center text-sm text-ink-700">
                        {row[key]}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center text-sm text-ink-700">
                      {getAccidentRate(row)}
                    </td>
                    {isOwner && (
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleAccidentEdit(row)}
                            className="text-xs text-brand-600 hover:text-brand-800"
                          >
                            수정
                          </button>
                          <button
                            onClick={() => row.id && accidentDeleteMutation.mutate(row.id)}
                            disabled={accidentDeleteMutation.isPending || !row.id}
                            className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-xs text-ink-400">
          * 근로자수는 근로자 현황에서 자동 반영 &nbsp;&nbsp; 계 = 사망 + 부상 + 직업병 (자동 계산) &nbsp;&nbsp; 재해율 = (재해자수 / 근로자수) &times; 100
        </p>
      </Card>
    </div>
  );
}

export default SiteBusinessOverviewTab;
